import { apiClient } from '../../lib/api/client'
import { multiplyAmount } from '../cart/cart-money'
import type { LibrarySourceable, VolumePricingCurve, VolumePricingTier } from './types'
import { problemTitle } from './usePartsLibrary'

/**
 * Volume-pricing curve helpers (UID-159 / A3, ADR-0058) — shared by the
 * Qty-cell popover (`VolumePricingPopover.tsx`) and the Part-detail pricing
 * card (`PartDetailPage.tsx`'s `PricingCard`/`AutoPricedCurveCard.tsx`).
 * Pure display-derivation only: the curve itself is server-derived and
 * ADVISORY (it mints no order-eligible `Price`), so nothing here writes a
 * price back — see the `VolumePricingCurve` doc comment on the generated
 * contract for the full rationale.
 */

/**
 * The tier that applies to a requested quantity: the tiers are ascending by
 * `minQuantity` (the base rung is `minQuantity: 1`), so the applicable rung
 * is the LAST one whose `minQuantity` is `<= qty`. Returns `undefined` only
 * for an empty tier list (never happens for a real `auto_priced` curve, but
 * a defensive return beats a thrown index error on a malformed fixture).
 */
export function findApplicableTier(
  tiers: readonly VolumePricingTier[],
  qty: number,
): VolumePricingTier | undefined {
  // qty < 1 isn't a real request quantity (0/negative) — fall through to
  // the base rung (`minQuantity: 1`) explicitly rather than relying on the
  // loop below finding no match. Same outcome either way; this documents
  // the intent instead of leaving it implicit.
  if (qty < 1) {
    return tiers[0]
  }
  let applicable: VolumePricingTier | undefined
  for (const tier of tiers) {
    if (tier.minQuantity <= qty) {
      applicable = tier
    }
  }
  return applicable ?? tiers[0]
}

/** Whether a requested quantity is beyond the curve's auto-price ceiling —
 * per ADR-0058 §3 the server never fabricates a price above `cap`, so the
 * FE must render the RFQ affordance instead of a price. */
export function isAboveCap(curve: VolumePricingCurve, qty: number): boolean {
  return qty > curve.cap
}

/**
 * The per-qty `total` the popover/panel render ("Qty {q} / {u} per unit") —
 * FE-DERIVED (`unitPrice × qty`), deliberately not a wire field (ADR-0058
 * §1: adding one would fork the tier shape). Goes through `multiplyAmount`'s
 * integer-cents math (ADR-0004), never a bare float multiply.
 */
export function computeTierTotal(unitPriceAmount: string, qty: number): string {
  return multiplyAmount(unitPriceAmount, qty)
}

/**
 * "Add {qty} to cart" (AC1) — the SAME `POST /cart/lines` path every other
 * add-to-cart entry point in the app uses (`toAddCartLineRequest` in
 * `verticals/cart/cart-catalog-lookup.ts`, reused by BOM/RFQ). Built
 * directly from the `LibrarySourceable` here rather than importing that
 * helper, because it types its first parameter as the full generated
 * `Sourceable` schema (indicativePrice/provenance/etc.) and this vertical's
 * `LibrarySourceable` is a narrower FE mirror — the two are NOT structurally
 * interchangeable even though they share the same `SourceableRef` fields
 * this call actually needs (id/kind/accountId/uom).
 *
 * Deliberately sends NO `price` — the curve is advisory display only
 * (ADR-0058 §4); this never "accepts" a volume-break price onto the line.
 * The BFF prices a plain catalog/custom add itself (ADR-0004), same as
 * every other non-accepted-quote add.
 */
export async function addLibraryPartToCart(
  sourceable: LibrarySourceable,
  qty: number,
): Promise<void> {
  const { error } = await apiClient.POST('/cart/lines', {
    body: {
      sourceable: {
        id: sourceable.id,
        kind: sourceable.kind,
        ...(sourceable.accountId !== undefined ? { accountId: sourceable.accountId } : {}),
        uom: sourceable.uom,
      },
      quantity: { value: qty, uom: sourceable.uom.saleUom },
      ...(sourceable.name !== undefined ? { displayName: sourceable.name } : {}),
      ...(sourceable.catalogSku !== undefined ? { partNumber: sourceable.catalogSku } : {}),
    },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t add this part to your cart. Try again.'))
  }
}
